import React, { useState, useEffect, useCallback } from 'react';
import {
    Page,
    Layout,
    Card,
    Modal,
    FormLayout,
    TextField,
    Select,
    Spinner,
    Banner,
    InlineStack,
    Filters,
    Pagination,
    Text,
    BlockStack
} from '@shopify/polaris';
import { ExpenseTable, ExpenseSummary } from '../components/accounting/ExpenseTable';
import { expenseService } from '../services/expenseService';
import {
    getExpenseCategoryOptions,
    getExpenseStatusOptions,
    accountingHelpers
} from '../constants/accountingConstants';
import { bankAccountService } from '../services/bankAccountService';

export const ExpensesPage = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [filters, setFilters] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [summary, setSummary] = useState(null);

    // Filter state
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Create expense form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'other',
        amount: '',
        vendor_name: '',
        expense_date: new Date().toISOString().split('T')[0],
        requires_approval: true
    });
    const [rejectReason, setRejectReason] = useState('');

    // Payment state
    const [showPayModal, setShowPayModal] = useState(false);
    const [payForm, setPayForm] = useState({
        payment_method: 'bank_transfer',
        payment_reference: '',
        payment_date: new Date().toISOString().split('T')[0],
        bank_account_id: ''
    });
    const [bankOptions, setBankOptions] = useState([]);

    const loadExpenses = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            setError(null);

            const result = await expenseService.getExpenses(filters, page, 20);
            setExpenses(result.expenses || []);
            setPagination(result.pagination || { page: 1, pages: 1, total: 0 });

            // Load summary for current month
            const now = new Date();
            const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

            const summaryData = await expenseService.getExpensesSummary(startDate, endDate, 'category');
            setSummary(summaryData);

        } catch (err) {
            console.error('Error loading expenses:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadExpenses();
    }, [loadExpenses]);

    const handleCreateExpense = async () => {
        try {
            setLoading(true);
            await expenseService.createExpense({
                ...formData,
                amount: parseFloat(formData.amount),
                currency: 'EGP'
            });

            setShowCreateModal(false);
            setFormData({
                title: '',
                description: '',
                category: 'other',
                amount: '',
                vendor_name: '',
                expense_date: new Date().toISOString().split('T')[0],
                requires_approval: true
            });

            loadExpenses();

        } catch (err) {
            console.error('Error creating expense:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!selectedExpense) return;

        try {
            setLoading(true);
            await expenseService.approveExpense(selectedExpense.expense_id);
            setShowApproveModal(false);
            setSelectedExpense(null);
            loadExpenses(pagination.page);
        } catch (err) {
            console.error('Error approving expense:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedExpense || !rejectReason.trim()) return;

        try {
            setLoading(true);
            await expenseService.rejectExpense(selectedExpense.expense_id, rejectReason);
            setShowRejectModal(false);
            setSelectedExpense(null);
            setRejectReason('');
            loadExpenses(pagination.page);
        } catch (err) {
            console.error('Error rejecting expense:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPayModal = async (expense) => {
        setSelectedExpense(expense);
        setPayForm({
            payment_method: expense.payment_method || 'bank_transfer',
            payment_reference: '',
            payment_date: new Date().toISOString().split('T')[0],
            bank_account_id: ''
        });
        setShowPayModal(true);

        // Load bank options if not loaded
        if (bankOptions.length === 0) {
            try {
                const options = await bankAccountService.getSelectOptions();
                setBankOptions(options.data || []);
            } catch (err) {
                console.error('Error loading bank options:', err);
            }
        }
    };

    const handlePay = async () => {
        if (!selectedExpense) return;

        try {
            setLoading(true);
            await expenseService.payExpense(selectedExpense.expense_id, {
                ...payForm,
                // If payment method is cash, bank_account_id might be optional or specific 'cash' account
                // For now, we enforce bank account selection for bank transfers
            });

            setShowPayModal(false);
            setSelectedExpense(null);
            loadExpenses(pagination.page);
        } catch (err) {
            console.error('Error paying expense:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };



    const handleClearFilters = () => {
        setCategoryFilter('');
        setStatusFilter('');
        setSearchQuery('');
        setFilters({});
    };

    const filterControls = [
        {
            key: 'category',
            label: 'Category',
            filter: (
                <Select
                    label="Category"
                    options={[
                        { label: 'All Categories', value: '' },
                        ...getExpenseCategoryOptions()
                    ]}
                    value={categoryFilter}
                    onChange={(value) => {
                        setCategoryFilter(value);
                        const newFilters = { ...filters };
                        if (value) {
                            newFilters.category = value;
                        } else {
                            delete newFilters.category;
                        }
                        setFilters(newFilters);
                    }}
                />
            ),
            shortcut: true
        },
        {
            key: 'status',
            label: 'Status',
            filter: (
                <Select
                    label="Status"
                    options={[
                        { label: 'All Statuses', value: '' },
                        ...getExpenseStatusOptions()
                    ]}
                    value={statusFilter}
                    onChange={(value) => {
                        setStatusFilter(value);
                        const newFilters = { ...filters };
                        if (value) {
                            newFilters.status = value;
                        } else {
                            delete newFilters.status;
                        }
                        setFilters(newFilters);
                    }}
                />
            ),
            shortcut: true
        }
    ];

    const appliedFilters = [];
    if (categoryFilter) {
        appliedFilters.push({
            key: 'category',
            label: `Category: ${accountingHelpers.getExpenseCategoryLabel(categoryFilter)}`,
            onRemove: () => {
                setCategoryFilter('');
                const newFilters = { ...filters };
                delete newFilters.category;
                setFilters(newFilters);
            }
        });
    }
    if (statusFilter) {
        appliedFilters.push({
            key: 'status',
            label: `Status: ${statusFilter}`,
            onRemove: () => {
                setStatusFilter('');
                const newFilters = { ...filters };
                delete newFilters.status;
                setFilters(newFilters);
            }
        });
    }

    return (
        <Page
            title="Expenses"
            subtitle="Manage and track business expenses"
            primaryAction={{
                content: 'Create Expense',
                onAction: () => setShowCreateModal(true)
            }}
            secondaryActions={[
                {
                    content: 'Refresh',
                    onAction: () => loadExpenses(pagination.page)
                }
            ]}
        >
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner tone="critical" title="Error">
                            <p>{error}</p>
                        </Banner>
                    </Layout.Section>
                )}

                {summary && (
                    <Layout.Section>
                        <Card>
                            <BlockStack gap="400">
                                <Text variant="headingMd" as="h2">
                                    Expenses Summary (Current Month)
                                </Text>
                                <InlineStack gap="400">
                                    <div>
                                        <Text variant="bodyLg" fontWeight="semibold">
                                            Total: {accountingHelpers.formatCurrency(summary.total_expenses)}
                                        </Text>
                                    </div>
                                </InlineStack>
                            </BlockStack>
                        </Card>
                    </Layout.Section>
                )}

                <Layout.Section>
                    <Card padding="0">
                        <div style={{ padding: '1rem' }}>
                            <Filters
                                queryValue={searchQuery}
                                filters={filterControls}
                                appliedFilters={appliedFilters}
                                onQueryChange={setSearchQuery}
                                onQueryClear={() => setSearchQuery('')}
                                onClearAll={handleClearFilters}
                            />
                        </div>

                        {loading && expenses.length === 0 ? (
                            <div style={{ padding: '3rem', textAlign: 'center' }}>
                                <Spinner size="large" />
                                <div style={{ marginTop: '1rem' }}>
                                    <Text>Loading expenses...</Text>
                                </div>
                            </div>
                        ) : (
                            <>
                                <ExpenseTable
                                    expenses={expenses}
                                    onApprove={(expense) => {
                                        setSelectedExpense(expense);
                                        setShowApproveModal(true);
                                    }}
                                    onReject={(expense) => {
                                        setSelectedExpense(expense);
                                        setShowRejectModal(true);
                                    }}
                                    onPay={handleOpenPayModal}
                                />

                                {pagination.pages > 1 && (
                                    <div style={{ padding: '1rem', borderTop: '1px solid #e1e3e5' }}>
                                        <InlineStack align="center">
                                            <Pagination
                                                hasPrevious={pagination.page > 1}
                                                onPrevious={() => loadExpenses(pagination.page - 1)}
                                                hasNext={pagination.page < pagination.pages}
                                                onNext={() => loadExpenses(pagination.page + 1)}
                                                label={`Page ${pagination.page} of ${pagination.pages}`}
                                            />
                                        </InlineStack>
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                </Layout.Section>

                {summary && summary.summary && summary.summary.length > 0 && (
                    <Layout.Section>
                        <ExpenseSummary summary={summary} />
                    </Layout.Section>
                )}
            </Layout>

            {/* Create Expense Modal */}
            <Modal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create Expense"
                primaryAction={{
                    content: 'Create',
                    onAction: handleCreateExpense,
                    loading: loading,
                    disabled: !formData.title || !formData.amount || !formData.category
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => setShowCreateModal(false)
                    }
                ]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            label="Title"
                            value={formData.title}
                            onChange={(value) => setFormData({ ...formData, title: value })}
                            placeholder="Enter expense title"
                            autoComplete="off"
                            requiredIndicator
                        />

                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(value) => setFormData({ ...formData, description: value })}
                            placeholder="Enter description"
                            multiline={3}
                            autoComplete="off"
                        />

                        <Select
                            label="Category"
                            options={getExpenseCategoryOptions()}
                            value={formData.category}
                            onChange={(value) => setFormData({ ...formData, category: value })}
                            requiredIndicator
                        />

                        <TextField
                            label="Amount (EGP)"
                            type="number"
                            value={formData.amount}
                            onChange={(value) => setFormData({ ...formData, amount: value })}
                            placeholder="0.00"
                            autoComplete="off"
                            requiredIndicator
                        />

                        <TextField
                            label="Vendor Name"
                            value={formData.vendor_name}
                            onChange={(value) => setFormData({ ...formData, vendor_name: value })}
                            placeholder="Enter vendor name"
                            autoComplete="off"
                        />

                        <TextField
                            label="Expense Date"
                            type="date"
                            value={formData.expense_date}
                            onChange={(value) => setFormData({ ...formData, expense_date: value })}
                            autoComplete="off"
                        />
                    </FormLayout>
                </Modal.Section>
            </Modal>

            {/* Approve Modal */}
            <Modal
                open={showApproveModal}
                onClose={() => setShowApproveModal(false)}
                title="Approve Expense"
                primaryAction={{
                    content: 'Approve',
                    onAction: handleApprove,
                    loading: loading,
                    tone: 'success'
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => setShowApproveModal(false)
                    }
                ]}
            >
                <Modal.Section>
                    <Text>
                        Are you sure you want to approve this expense?
                    </Text>
                    {selectedExpense && (
                        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                            <Text variant="bodyMd" fontWeight="semibold">
                                {selectedExpense.title}
                            </Text>
                            <Text variant="bodyMd">
                                Amount: {accountingHelpers.formatCurrency(selectedExpense.amount)}
                            </Text>
                        </div>
                    )}
                </Modal.Section>
            </Modal>

            {/* Reject Modal */}
            <Modal
                open={showRejectModal}
                onClose={() => setShowRejectModal(false)}
                title="Reject Expense"
                primaryAction={{
                    content: 'Reject',
                    onAction: handleReject,
                    loading: loading,
                    tone: 'critical',
                    disabled: !rejectReason.trim()
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => setShowRejectModal(false)
                    }
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        <Text>
                            Please provide a reason for rejecting this expense.
                        </Text>
                        {selectedExpense && (
                            <div style={{ padding: '1rem', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                                <Text variant="bodyMd" fontWeight="semibold">
                                    {selectedExpense.title}
                                </Text>
                                <Text variant="bodyMd">
                                    Amount: {accountingHelpers.formatCurrency(selectedExpense.amount)}
                                </Text>
                            </div>
                        )}
                        <TextField
                            label="Rejection Reason"
                            value={rejectReason}
                            onChange={setRejectReason}
                            placeholder="Enter reason for rejection"
                            multiline={3}
                            autoComplete="off"
                            requiredIndicator
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Pay Expense Modal */}
            <Modal
                open={showPayModal}
                onClose={() => setShowPayModal(false)}
                title="Record Payment"
                primaryAction={{
                    content: 'Confirm Payment',
                    onAction: handlePay,
                    loading: loading,
                    disabled: !payForm.payment_method || (payForm.payment_method !== 'cash' && !payForm.bank_account_id)
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => setShowPayModal(false)
                    }
                ]}
            >
                <Modal.Section>
                    {selectedExpense && (
                        <BlockStack gap="400">
                            <div style={{ padding: '1rem', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                                <BlockStack gap="200">
                                    <Text variant="bodyMd" fontWeight="semibold">{selectedExpense.title}</Text>
                                    <InlineStack align="space-between">
                                        <Text>Amount Due:</Text>
                                        <Text fontWeight="bold">{accountingHelpers.formatCurrency(selectedExpense.amount)}</Text>
                                    </InlineStack>
                                </BlockStack>
                            </div>

                            <FormLayout>
                                <Select
                                    label="Payment Method"
                                    options={[
                                        { label: 'Bank Transfer', value: 'bank_transfer' },
                                        { label: 'Cash', value: 'cash' },
                                        { label: 'Check', value: 'check' },
                                        { label: 'Credit Card', value: 'credit_card' },
                                        { label: 'Other', value: 'other' }
                                    ]}
                                    value={payForm.payment_method}
                                    onChange={(value) => setPayForm({ ...payForm, payment_method: value })}
                                    requiredIndicator
                                />

                                {payForm.payment_method !== 'cash' && (
                                    <Select
                                        label="Bank Account (Source)"
                                        options={[
                                            { label: 'Select account...', value: '' },
                                            ...bankOptions
                                        ]}
                                        value={payForm.bank_account_id}
                                        onChange={(value) => setPayForm({ ...payForm, bank_account_id: value })}
                                        requiredIndicator
                                    />
                                )}

                                <TextField
                                    label="Payment Date"
                                    type="date"
                                    value={payForm.payment_date}
                                    onChange={(value) => setPayForm({ ...payForm, payment_date: value })}
                                    autoComplete="off"
                                    requiredIndicator
                                />

                                <TextField
                                    label="Payment Reference"
                                    value={payForm.payment_reference}
                                    onChange={(value) => setPayForm({ ...payForm, payment_reference: value })}
                                    placeholder="e.g. Transaction ID, Check #"
                                    autoComplete="off"
                                />
                            </FormLayout>
                        </BlockStack>
                    )}
                </Modal.Section>
            </Modal>
        </Page>
    );
};

export default ExpensesPage;
